import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Clock, Download, X } from 'lucide-react';
import type { CRMUser } from '../../types';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import {
  type CrmHoursPeriod,
  type CrmHoursRow,
  CRM_BUSINESS_END_HOUR,
  CRM_BUSINESS_START_HOUR,
  CRM_HOURS_FALLBACK_TZ,
  aggregateCrmHoursRows,
  formatCrmExtraPct,
  formatCrmMinutes,
} from '../../utils/crmActivityHours';
import { fetchCrmHoursDocs } from '../../utils/crmActivityService';
import {
  activityGroupEnd,
  activityGroupStart,
  buildActivityLogDisplayGroups,
  sumMinutesInActivityGroup,
} from '../../utils/activityLogDisplayGroups';
import {
  type CeoTimeCostReport,
  buildCeoTimeCostCsv,
  buildCeoTimeCostReport,
  fetchCeoTimeCostSources,
  formatCeoCostUsd,
} from '../../utils/ceoTimeCostReport';
import {
  type TeamActivityPeriod,
  type TeamActivityReport,
  buildTeamActivityCsv,
  buildTeamActivityReport,
  fetchTeamActivitySources,
  formatActivityHours,
  teamActivityPeriodRange,
} from '../../utils/teamActivityReport';

type ReportTab = 'ceo' | 'costing' | 'log' | 'presence';

const COSTING_PERIODS: Array<{ id: TeamActivityPeriod; label: string }> = [
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'custom', label: 'Custom' },
];

const PRESENCE_PERIODS: Array<{ id: CrmHoursPeriod; label: string }> = [
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'year', label: 'Year' },
  { id: 'all', label: 'All time' },
];

interface TeamActivityReportModalProps {
  users: CRMUser[];
  onClose: () => void;
}

function toInputDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatWhen(d: Date): string {
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const TeamActivityReportModal: React.FC<TeamActivityReportModalProps> = ({ users, onClose }) => {
  const { user: authUser } = useAuth();
  const { timberProjects, leads, enhancedDeals } = useCRM();
  const [tab, setTab] = useState<ReportTab>('ceo');
  const [period, setPeriod] = useState<TeamActivityPeriod>('week');
  const defaultRange = useMemo(() => teamActivityPeriodRange('week'), []);
  const [customStart, setCustomStart] = useState(toInputDate(defaultRange.start));
  const [customEnd, setCustomEnd] = useState(toInputDate(defaultRange.end));
  const [report, setReport] = useState<TeamActivityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [logUserId, setLogUserId] = useState<string>('all');

  const [presencePeriod, setPresencePeriod] = useState<CrmHoursPeriod>('week');
  const [presenceRows, setPresenceRows] = useState<CrmHoursRow[]>([]);
  const [presenceLoading, setPresenceLoading] = useState(true);
  const [presenceError, setPresenceError] = useState<string | null>(null);

  const [ceoReport, setCeoReport] = useState<CeoTimeCostReport | null>(null);
  const [ceoLoading, setCeoLoading] = useState(true);
  const [ceoError, setCeoError] = useState<string | null>(null);
  const [ceoUserId, setCeoUserId] = useState<string>('self');
  const [hourlyRateInput, setHourlyRateInput] = useState('');

  const range = useMemo(() => {
    if (period === 'custom' && customStart && customEnd) {
      return {
        start: new Date(`${customStart}T00:00:00`),
        end: new Date(`${customEnd}T23:59:59`),
      };
    }
    return teamActivityPeriodRange(period === 'custom' ? 'week' : period);
  }, [period, customStart, customEnd]);

  const hourlyRateUsd = useMemo(() => {
    const trimmed = hourlyRateInput.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    return parsed;
  }, [hourlyRateInput]);

  const resolvedCeoUserId = useMemo(() => {
    if (ceoUserId === 'all') return undefined;
    if (ceoUserId === 'self') return authUser?.id;
    return ceoUserId;
  }, [ceoUserId, authUser?.id]);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchTeamActivitySources(range)
      .then((sources) => {
        if (cancelled) return;
        setReport(buildTeamActivityReport({
          range,
          ...sources,
          projects: timberProjects,
          leads,
          deals: enhancedDeals,
        }));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError(err instanceof Error ? err.message : 'Could not load activity.');
        setReport(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, timberProjects, leads, enhancedDeals]);

  useEffect(() => {
    let cancelled = false;
    setCeoLoading(true);
    setCeoError(null);
    void fetchCeoTimeCostSources(range, resolvedCeoUserId)
      .then(({ heartbeats }) => {
        if (cancelled) return;
        setCeoReport(buildCeoTimeCostReport({
          range,
          heartbeats,
          projects: timberProjects,
          leads,
          deals: enhancedDeals,
          hourlyRateUsd,
          userId: resolvedCeoUserId,
        }));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setCeoError(err instanceof Error ? err.message : 'Could not load CEO time-cost data.');
        setCeoReport(null);
      })
      .finally(() => {
        if (!cancelled) setCeoLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, resolvedCeoUserId, timberProjects, leads, enhancedDeals, hourlyRateUsd]);

  useEffect(() => {
    let cancelled = false;
    setPresenceLoading(true);
    setPresenceError(null);
    void fetchCrmHoursDocs(presencePeriod)
      .then((docs) => {
        if (cancelled) return;
        setPresenceRows(aggregateCrmHoursRows(docs, users));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setPresenceError(err instanceof Error ? err.message : 'Could not load CRM hours.');
        setPresenceRows([]);
      })
      .finally(() => {
        if (!cancelled) setPresenceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [presencePeriod, users]);

  const toggleProject = useCallback((projectId: string) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  }, []);

  const downloadCsv = useCallback(() => {
    if (!report) return;
    const blob = new Blob([buildTeamActivityCsv(report)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lctf-activity-${toInputDate(report.range.start)}-${toInputDate(report.range.end)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [report]);

  const downloadCeoCsv = useCallback(() => {
    if (!ceoReport) return;
    const blob = new Blob([buildCeoTimeCostCsv(ceoReport)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lctf-ceo-time-cost-${toInputDate(ceoReport.range.start)}-${toInputDate(ceoReport.range.end)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [ceoReport]);

  const logEntries = useMemo(() => {
    const all = report?.activityLog ?? [];
    return logUserId === 'all' ? all : all.filter((e) => e.userId === logUserId);
  }, [report, logUserId]);

  const logGroups = useMemo(() => buildActivityLogDisplayGroups(logEntries), [logEntries]);

  return (
    <div className="um-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="um-modal um-hours-modal um-activity-modal"
        role="dialog"
        aria-labelledby="team-activity-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="um-modal-header">
          <h3 id="team-activity-title">
            <Clock size={16} aria-hidden /> Activity report
          </h3>
          <div className="um-activity-header-actions">
            {tab === 'ceo' && ceoReport && (
              <button type="button" className="um-action-btn" onClick={downloadCeoCsv}>
                <Download size={13} /> CSV
              </button>
            )}
            {tab === 'costing' && report && (
              <button type="button" className="um-action-btn" onClick={downloadCsv}>
                <Download size={13} /> CSV
              </button>
            )}
            <button type="button" className="um-modal-close" onClick={onClose} aria-label="Close activity report">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="um-modal-body">
          <p className="um-hours-help">
            Idle-aware CRM time (tab visible, recent input). CEO costing uses session
            heartbeats with job/deal tags when present. Job costing uses Project Hub and
            lead/proposal sessions. Other pages count as overhead.
          </p>

          <div className="um-hours-periods" role="tablist" aria-label="Report view">
            {([
              { id: 'ceo', label: 'CEO costing' },
              { id: 'costing', label: 'Job costing' },
              { id: 'log', label: 'Activity log' },
              { id: 'presence', label: 'Presence' },
            ] as const).map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                className={`um-hours-period${tab === item.id ? ' is-active' : ''}`}
                onClick={() => setTab(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>

          {tab !== 'presence' && (
            <>
              <div className="um-hours-periods" role="tablist" aria-label="Report period">
                {COSTING_PERIODS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={period === item.id}
                    className={`um-hours-period${period === item.id ? ' is-active' : ''}`}
                    onClick={() => setPeriod(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              {period === 'custom' && (
                <div className="um-activity-custom-range">
                  <label>
                    From
                    <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                  </label>
                  <label>
                    To
                    <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
                  </label>
                </div>
              )}
            </>
          )}

          {tab === 'ceo' && (
            <div className="um-activity-stack">
              <div className="um-activity-custom-range">
                <label>
                  Person
                  <select value={ceoUserId} onChange={(e) => setCeoUserId(e.target.value)}>
                    <option value="self">Me{authUser?.name ? ` (${authUser.name})` : ''}</option>
                    <option value="all">Everyone</option>
                    {users.map((u) => (
                      <option key={u.uid} value={u.uid}>{u.displayName || u.email || u.uid}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Placeholder $/hr
                  <input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Unset"
                    value={hourlyRateInput}
                    onChange={(e) => setHourlyRateInput(e.target.value)}
                  />
                </label>
              </div>
              <p className="um-hours-help">
                Rate is a placeholder for Matthew/CFO iteration only — not an approved production
                rate. Heartbeats ignore background tabs and sessions under 15 seconds. Does not
                write to Google Calendar.
              </p>
              {ceoError && <div className="um-hours-error">{ceoError}</div>}
              {ceoLoading && <p className="um-hours-empty">Loading…</p>}
              {!ceoLoading && ceoReport && (
                <>
                  <div className="um-activity-summary">
                    <span>Total {formatActivityHours(ceoReport.totalMinutes)}</span>
                    <span>Jobs {formatActivityHours(ceoReport.jobMinutes)}</span>
                    <span>Untagged {formatActivityHours(ceoReport.untaggedMinutes)}</span>
                    {ceoReport.totalEstimatedCost != null && (
                      <span>Est. {formatCeoCostUsd(ceoReport.totalEstimatedCost)}</span>
                    )}
                  </div>

                  <h4 className="um-activity-section-title">By job / deal</h4>
                  {ceoReport.byJob.length === 0 ? (
                    <p className="um-hours-empty">No job-tagged beacon time in this period.</p>
                  ) : (
                    <div className="um-hours-table-wrap">
                      <table className="um-hours-table" aria-label="CEO time by job">
                        <thead>
                          <tr>
                            <th>Job / deal</th>
                            <th className="right">Hours</th>
                            <th className="right">Est. cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ceoReport.byJob.map((row) => (
                            <tr key={row.key}>
                              <td>{row.label}</td>
                              <td className="right">{formatActivityHours(row.minutes)}</td>
                              <td className="right">{formatCeoCostUsd(row.estimatedCost)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <h4 className="um-activity-section-title">Untagged / overhead</h4>
                  <div className="um-activity-summary">
                    <span>{formatActivityHours(ceoReport.untaggedMinutes)}</span>
                    {ceoReport.untaggedEstimatedCost != null && (
                      <span>{formatCeoCostUsd(ceoReport.untaggedEstimatedCost)}</span>
                    )}
                  </div>

                  <h4 className="um-activity-section-title">By activity class</h4>
                  {ceoReport.byClass.length === 0 ? (
                    <p className="um-hours-empty">No beacon sessions in this period.</p>
                  ) : (
                    <div className="um-hours-table-wrap">
                      <table className="um-hours-table" aria-label="CEO time by activity class">
                        <thead>
                          <tr>
                            <th>Class</th>
                            <th className="right">Hours</th>
                            <th className="right">Est. cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ceoReport.byClass.map((row) => (
                            <tr key={row.activityClass}>
                              <td>{row.label}</td>
                              <td className="right">{formatActivityHours(row.minutes)}</td>
                              <td className="right">{formatCeoCostUsd(row.estimatedCost)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {tab !== 'presence' && tab !== 'ceo' && error && <div className="um-hours-error">{error}</div>}
          {tab !== 'presence' && tab !== 'ceo' && loading && <p className="um-hours-empty">Loading…</p>}

          {tab === 'costing' && !loading && report && (
            <div className="um-activity-stack">
              <div className="um-activity-summary">
                <span>Jobs {formatActivityHours(report.attributedMinutes)}</span>
                <span>Overhead {formatActivityHours(report.overheadMinutes)}</span>
              </div>

              <h4 className="um-activity-section-title">By person</h4>
              {report.byPerson.length === 0 ? (
                <p className="um-hours-empty">No activity in this period.</p>
              ) : (
                <div className="um-hours-table-wrap">
                  <table className="um-hours-table" aria-label="Hours by person">
                    <thead>
                      <tr>
                        <th>Person</th>
                        <th className="right">Jobs</th>
                        <th className="right">Overhead</th>
                        <th className="right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.byPerson.map((row) => (
                        <tr key={row.userId}>
                          <td>{row.userName}</td>
                          <td className="right">{formatActivityHours(row.attributedMinutes)}</td>
                          <td className="right">{formatActivityHours(row.overheadMinutes)}</td>
                          <td className="right">{formatActivityHours(row.totalMinutes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h4 className="um-activity-section-title">Projects</h4>
              {report.projects.length === 0 ? (
                <p className="um-hours-empty">No project time in this period.</p>
              ) : (
                <div className="um-hours-table-wrap">
                  <table className="um-hours-table" aria-label="Hours by project">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Billing</th>
                        <th className="right">Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.projects.map((row) => {
                        const open = expandedProjects.has(row.projectId);
                        return (
                          <React.Fragment key={row.projectId}>
                            <tr>
                              <td>
                                <button
                                  type="button"
                                  className="um-activity-expand"
                                  onClick={() => toggleProject(row.projectId)}
                                  aria-expanded={open}
                                >
                                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  <span>
                                    {row.projectName}
                                    {row.clientName ? <span className="um-activity-muted"> · {row.clientName}</span> : null}
                                  </span>
                                </button>
                              </td>
                              <td>{row.billingLabel}</td>
                              <td className="right">{formatActivityHours(row.minutes)}</td>
                            </tr>
                            {open && (
                              <tr className="um-activity-nested">
                                <td colSpan={3}>
                                  <div className="um-activity-user-panel">
                                    {row.leadMinutes > 0 && (
                                      <p className="um-activity-muted">
                                        Includes {formatActivityHours(row.leadMinutes)} lead/proposal time
                                      </p>
                                    )}
                                    {row.byUser.map((user) => (
                                      <div key={user.userId} className="um-activity-user-row">
                                        <span>{user.userName}</span>
                                        <span>{formatActivityHours(user.minutes)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <h4 className="um-activity-section-title">Leads &amp; proposals</h4>
              {report.leads.length === 0 ? (
                <p className="um-hours-empty">No unassigned lead time in this period.</p>
              ) : (
                <div className="um-hours-table-wrap">
                  <table className="um-hours-table" aria-label="Hours by lead">
                    <thead>
                      <tr>
                        <th>Lead</th>
                        <th className="right">Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.leads.map((row) => (
                        <tr key={row.leadId}>
                          <td>
                            {row.leadName}
                            <div className="um-activity-user-panel">
                              {row.byUser.map((user) => (
                                <div key={user.userId} className="um-activity-user-row">
                                  <span>{user.userName}</span>
                                  <span>{formatActivityHours(user.minutes)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="right">{formatActivityHours(row.minutes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h4 className="um-activity-section-title">Overhead (not charged to a job)</h4>
              {report.overhead.length === 0 ? (
                <p className="um-hours-empty">No unallocated CRM time in this period.</p>
              ) : (
                <div className="um-hours-table-wrap">
                  <table className="um-hours-table" aria-label="Overhead hours">
                    <thead>
                      <tr>
                        <th>Person</th>
                        <th>Pages</th>
                        <th className="right">Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.overhead.map((row) => (
                        <tr key={row.userId}>
                          <td>{row.userName}</td>
                          <td className="um-activity-muted">
                            {row.byPage.map((p) => `${p.pageLabel} ${formatActivityHours(p.minutes)}`).join(' · ')}
                          </td>
                          <td className="right">{formatActivityHours(row.minutes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 'log' && !loading && report && (
            <div className="um-activity-stack">
              <label className="um-activity-log-filter">
                Person
                <select value={logUserId} onChange={(e) => setLogUserId(e.target.value)}>
                  <option value="all">Everyone</option>
                  {report.byPerson.map((row) => (
                    <option key={row.userId} value={row.userId}>{row.userName}</option>
                  ))}
                </select>
              </label>
              {logGroups.length === 0 ? (
                <p className="um-hours-empty">No sessions in this period.</p>
              ) : (
                <ul className="um-activity-log">
                  {logGroups.map((group) => {
                    const head = group.kind === 'single' ? group.entry : group.entries[0];
                    const minutes = sumMinutesInActivityGroup(group);
                    const key = group.kind === 'single'
                      ? head.id
                      : group.entries.map((e) => e.id).join('|');
                    return (
                      <li key={key} className="um-activity-log-row">
                        <div>
                          <strong>{head.userName}</strong>
                          <span className="um-activity-muted">
                            {' '}{head.pageLabel}
                            {head.entityLabel ? ` · ${head.entityLabel}` : ''}
                            {group.kind === 'merged' ? ` · ${group.entries.length} slices` : ''}
                          </span>
                        </div>
                        <div className="um-activity-log-meta">
                          <span>{formatWhen(activityGroupStart(group))} – {formatWhen(activityGroupEnd(group))}</span>
                          <span>{formatActivityHours(minutes)}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {tab === 'presence' && (
            <div className="um-activity-stack">
              <p className="um-hours-help">
                Active CRM time split by each user’s timezone. Extra is before {CRM_BUSINESS_START_HOUR}:00,
                after {CRM_BUSINESS_END_HOUR}:00, or weekends ({CRM_HOURS_FALLBACK_TZ} when unset).
                Planning signal — not a timesheet.
              </p>
              <div className="um-hours-periods" role="tablist" aria-label="Presence period">
                {PRESENCE_PERIODS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={presencePeriod === item.id}
                    className={`um-hours-period${presencePeriod === item.id ? ' is-active' : ''}`}
                    onClick={() => setPresencePeriod(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              {presenceError && <div className="um-hours-error">{presenceError}</div>}
              {presenceLoading ? (
                <p className="um-hours-empty">Loading…</p>
              ) : presenceRows.length === 0 ? (
                <p className="um-hours-empty">No CRM hours recorded in this period.</p>
              ) : (
                <div className="um-hours-table-wrap">
                  <table className="um-hours-table" aria-label="CRM hours by user">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Timezone</th>
                        <th className="right">In hours</th>
                        <th className="right">After hours</th>
                        <th className="right">Extra</th>
                      </tr>
                    </thead>
                    <tbody>
                      {presenceRows.map((row) => (
                        <tr key={row.userId}>
                          <td>{row.displayName}</td>
                          <td>
                            {row.timezoneFallback
                              ? `${CRM_HOURS_FALLBACK_TZ} fallback`
                              : row.timezone}
                          </td>
                          <td className="right">{formatCrmMinutes(row.minutesInHours)}</td>
                          <td className="right">{formatCrmMinutes(row.minutesAfterHours)}</td>
                          <td className="right">{formatCrmExtraPct(row.extraPct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamActivityReportModal;
